import { CreatorPermission, FeatureFlagName } from '@api'

import AccountingIcon from '@/assets/icons/icon-accounting.svg'
import AiFeaturesIcon from '@/assets/icons/icon-ai-features.svg'
import AiTranslatorIcon from '@/assets/icons/icon-ai-translator.svg'
import AutoFollowIcon from '@/assets/icons/icon-auto-follow.svg'
import AutomatedMessagesIcon from '@/assets/icons/icon-automated-messages.svg'
import AutomationIcon from '@/assets/icons/icon-automations.svg'
import IconBrandMask from '@/assets/icons/icon-brand-mask.svg'
import RebillMessageIcon from '@/assets/icons/icon-calendar-repeat.svg'
import ChatterTrackingIcon from '@/assets/icons/icon-chatter-tracking.svg'
import ClockIcon from '@/assets/icons/icon-clock.svg'
import CreatorSettingsIcon from '@/assets/icons/icon-creator-settings.svg'
import CreatorTransferIcon from '@/assets/icons/icon-creator-transfer.svg'
import CustomListsIcon from '@/assets/icons/icon-custom-lists.svg'
import DisplaySettingsIcon from '@/assets/icons/icon-display-settings.svg'
import NoSpendersIcon from '@/assets/icons/icon-dollar-crossed.svg'
import ExpiringFansIcon from '@/assets/icons/icon-expired.svg'
import FanNumberingIcon from '@/assets/icons/icon-fan-numbering.svg'
import GiftBagIcon from '@/assets/icons/icon-gift-bag.svg'
import GlobeSearchIcon from '@/assets/icons/icon-globe-search.svg'
import GlobeIcon from '@/assets/icons/icon-globe.svg'
import GroupsIcon from '@/assets/icons/icon-groups.svg'
import HomeIcon from '@/assets/icons/icon-home.svg'
import InstagramStatisticsIcon from '@/assets/icons/icon-instagram.svg'
import IconKey from '@/assets/icons/icon-key.svg'
import LicenseIcon from '@/assets/icons/icon-licence.svg'
import ManageCreatorsIcon from '@/assets/icons/icon-manage-creators.svg'
import IconMegaphone from '@/assets/icons/icon-megaphone.svg'
import MessageHistoryIcon from '@/assets/icons/icon-message-history.svg'
import NewSubscriberIcon from '@/assets/icons/icon-new-subscriber.svg'
import OnlineMassMessageIcon from '@/assets/icons/icon-online-mass-message.svg'
import OFStatisticsIcon from '@/assets/icons/icon-onlyfans.svg'
import IconPerson from '@/assets/icons/icon-person.svg'
import PPVTrackingIcon from '@/assets/icons/icon-ppv-tracking.svg'
import PromotionReactivatorIcon from '@/assets/icons/icon-promotion-reactivator.svg'
import ProspectsIcon from '@/assets/icons/icon-prospects.svg'
import RecentSpendersIcon from '@/assets/icons/icon-recent-spend-list.svg'
import RestrictedWordsIcon from '@/assets/icons/icon-restricted-words.svg'
import ScriptsIcon from '@/assets/icons/icon-scripts.svg'
import SettingsIcon from '@/assets/icons/icon-settings.svg'
import IconShield from '@/assets/icons/icon-shield.svg'
import SparklesIcon from '@/assets/icons/icon-sparkles.svg'
import IconSpendLIst from '@/assets/icons/icon-spend-list.svg'
import SupportCenterIcon from '@/assets/icons/icon-support-center.svg'
import TasksIcon from '@/assets/icons/icon-tasks.svg'
import IconTikTokLists from '@/assets/icons/icon-tiktok-lists.svg'
import IconTikTokResearch from '@/assets/icons/icon-tiktok-research.svg'
import IconTikTokSounds from '@/assets/icons/icon-tiktok-sounds.svg'
import IconTikTokVideos from '@/assets/icons/icon-tiktok-videos.svg'
import TikTokStatisticsIcon from '@/assets/icons/icon-tiktok.svg'
import TrackingIcon from '@/assets/icons/icon-tracking.svg'
import UserAddIcon from '@/assets/icons/icon-user-add.svg'
import VaultIcon from '@/assets/icons/icon-vault.svg'
import ListsIcon from '@/assets/icons/icon-view-list.svg'
import VipSupportIcon from '@/assets/icons/icon-vip-support.svg'
import VisibilityIcon from '@/assets/icons/icon-visibility.svg'
import type { NavSingleButtonProps } from '@/components/layout/sidebar/nav/nav-single-button.tsx'
import { CreatorFeatures } from '@/modules/accounts/types'
import { AppRoutes, creatorIdPlaceholder } from '@/utils/constants'

import type { SubRoute } from '../sub-sidebar.tsx'

interface NavSection {
  separator: 'marginTop' | 'border'
  items: NavSingleButtonProps[]
}

export const SIDEBAR_ITEMS: NavSection[] = [
  {
    separator: 'border',
    items: [
      {
        title: 'Overview',
        href: AppRoutes.Home.asUrl(),
        Icon: HomeIcon,
        testId: 'overview',
      },
      {
        title: 'Tasks',
        href: AppRoutes.Tasks.List.asUrl(),
        Icon: TasksIcon,
        accountFeature: CreatorFeatures.tasks,
        testId: 'tasks',
      },
      {
        title: 'Team',
        href: AppRoutes.Teams.Members.List.asUrl(),
        Icon: GroupsIcon,
        onlyAdmin: true,
        testId: 'team',
      },
      {
        title: 'Roles',
        href: AppRoutes.Teams.Roles.List.asUrl(),
        Icon: IconKey,
        onlyAdmin: true,
        testId: 'roles',
      },
      {
        title: 'Shifts',
        href: AppRoutes.Shifts.List.asUrl(),
        Icon: ClockIcon,
        testId: 'shifts',
      },
      {
        title: 'Creators',
        href: AppRoutes.Creators.List.asUrl(),
        Icon: ManageCreatorsIcon,
        testId: 'creators',
      },
      {
        title: 'Blacklisted Words',
        href: AppRoutes.RestrictedWords.List.asUrl(),
        Icon: RestrictedWordsIcon,
        onlyAdmin: true,
        testId: 'blacklisted-words',
      },
      {
        title: 'Prospects',
        href: AppRoutes.Prospects.List.asUrl(),
        Icon: ProspectsIcon,
        accountFeature: CreatorFeatures.prospecting,
        onlyAdmin: true,
        testId: 'prospects',
      },
    ],
  },
  {
    separator: 'border',
    items: [
      {
        title: 'Statistics',
        Icon: VisibilityIcon,
        testId: 'statistics',
        href: '#',
        children: [
          {
            title: 'OnlyFans',
            href: AppRoutes.Creator.Statistics.OnlyFans.asUrl(
              creatorIdPlaceholder,
            ),
            testId: 'of-statistics',
            Icon: OFStatisticsIcon,
            creatorPermission: CreatorPermission.SEE_STATS,
            creatorFeature: CreatorFeatures.stats,
          },
          {
            title: 'TikTok',
            href: AppRoutes.Creator.Statistics.TikTok.asUrl(
              creatorIdPlaceholder,
            ),
            testId: 'tiktok-statistics',
            Icon: TikTokStatisticsIcon,
            creatorPermission: CreatorPermission.MODIFY_SOCIAL_ACCOUNTS,
            creatorFeature: CreatorFeatures.tiktok,
          },
          {
            title: 'Instagram Reels',
            href: AppRoutes.Creator.Statistics.InstagramReels.asUrl(
              creatorIdPlaceholder,
            ),
            testId: 'instagram-statistics',
            Icon: InstagramStatisticsIcon,
            creatorPermission: CreatorPermission.MODIFY_SOCIAL_ACCOUNTS,
            creatorFeature: CreatorFeatures.instagram,
          },
        ],
      },
      {
        title: 'Tracking',
        Icon: TrackingIcon,
        testId: 'tracking',
        href: '#',
        children: [
          {
            title: 'Funnel Monitoring',
            href: AppRoutes.Creator.Funnel.Index.asUrl(creatorIdPlaceholder),
            Icon: GlobeSearchIcon,
            // TODO: Add proper permissions and features
            creatorFeature: CreatorFeatures.tracking,
            creatorPermission: CreatorPermission.MODIFY_SOCIAL_ACCOUNTS,
            testId: 'funnel-monitoring',
          },
          {
            title: 'Chatter Tracking',
            href: AppRoutes.Creator.ChatterTracking.Chatter.List.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: ChatterTrackingIcon,
            creatorPermission: CreatorPermission.SEE_CHATTER_TRACKING,
            creatorFeature: CreatorFeatures.tracking,
            testId: 'chatter-tracking',
          },
          {
            title: 'New Subscriber Analysis',
            href: AppRoutes.Creator.ChatterTracking.NewSubscriberAnalysis.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: NewSubscriberIcon,
            creatorFeature: CreatorFeatures.script_tracking,
            creatorPermission: CreatorPermission.SEE_STATS,
            testId: 'new-subscriber-analysis',
          },
          {
            title: 'PPV Tracking',
            href: AppRoutes.Creator.ChatterTracking.Ppv.List.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: PPVTrackingIcon,
            creatorFeature: CreatorFeatures.script_tracking,
            creatorPermission: CreatorPermission.SEE_STATS,
            testId: 'ppv-tracking',
          },
          {
            title: 'Tracking Links',
            href: AppRoutes.Creator.ChatterTracking.Links.List.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: GlobeIcon,
            creatorFeature: CreatorFeatures.tracking,
            creatorPermission: CreatorPermission.SEE_STATS,
            testId: 'tracking-links',
          },
          {
            title: 'Free Trial Links',
            href: AppRoutes.Creator.ChatterTracking.FreeTrialLinks.List.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: GiftBagIcon,
            creatorFeature: CreatorFeatures.tracking,
            creatorPermission: CreatorPermission.SEE_STATS,
            testId: 'free-trial-links',
          },
          {
            title: 'Message History',
            href: AppRoutes.Creator.ChatterTracking.MessageHistory.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: MessageHistoryIcon,
            creatorPermission: CreatorPermission.SEE_CHATTER_TRACKING,
            creatorFeature: CreatorFeatures.tracking,
            testId: 'message-history',
          },
        ],
      },
      {
        title: 'AI Features',
        Icon: AiFeaturesIcon,
        testId: 'ai-features',
        href: '#',
        children: [
          {
            title: 'AI Translator',
            href: AppRoutes.Creator.AiFeatures.AiSettings.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: AiTranslatorIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            creatorFeature: CreatorFeatures.display_settings,
            testId: 'ai-settings',
          },
          {
            title: 'AICH Alpha',
            href: AppRoutes.Creator.AiFeatures.AICHAlpha.asUrl(
              creatorIdPlaceholder,
            ),
            featureFlagName: FeatureFlagName.AI_CHATTING_FEATURE,
            Icon: SparklesIcon,
            onlyAdmin: true,
            testId: 'ai-chatting-settings',
          },
        ],
      },
      {
        title: 'Creator Settings',
        href: '#',
        Icon: CreatorSettingsIcon,
        testId: 'creator-settings',
        children: [
          {
            title: 'Automations',
            href: AppRoutes.Creator.Settings.Automations.Promotion.asUrl(
              creatorIdPlaceholder,
            ),
            creatorFeature: CreatorFeatures.promotion_automations,
            hoverRoutes: [
              AppRoutes.Creator.Settings.Automations.Promotion.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Automations.AutoFollow.asUrl(
                creatorIdPlaceholder,
              ),
            ],
            Icon: AutomationIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'creator-automations',
          },
          {
            title: 'Fan Numbering',
            href: AppRoutes.Creator.Settings.Automations.FanNumbering.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: FanNumberingIcon,
            creatorFeature: CreatorFeatures.fan_numbering,
            creatorPermission: CreatorPermission.MODIFY_FAN_NUMBERING,
            testId: 'fan-numbering',
          },
          {
            title: 'Scripts',
            href: AppRoutes.Creator.Settings.Scripts.List.asUrl(
              creatorIdPlaceholder,
            ),
            creatorFeature: CreatorFeatures.scripts,
            Icon: ScriptsIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'scripts',
          },
          {
            title: 'Automated Messages',
            href: AppRoutes.Creator.Settings.WelcomeMessages.asUrl(
              creatorIdPlaceholder,
            ),
            hoverRoutes: [
              AppRoutes.Creator.Settings.WelcomeMessages.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.PpvFollowUpMessages.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.RebillMessages.asUrl(
                creatorIdPlaceholder,
              ),
            ],
            Icon: AutomatedMessagesIcon,
            creatorFeature: CreatorFeatures.welcome_messages,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'automated-messages',
          },
          {
            title: 'Lists',
            href: AppRoutes.Creator.Settings.Lists.NewSubsList.asUrl(
              creatorIdPlaceholder,
            ),
            hoverRoutes: [
              AppRoutes.Creator.Settings.Lists.NewSubsList.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Lists.SpendingLists.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Lists.NoSpenders.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Lists.RecentSpenders.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Lists.SharedFollowers.asUrl(
                creatorIdPlaceholder,
              ),
              AppRoutes.Creator.Settings.Lists.CustomLists.asUrl(
                creatorIdPlaceholder,
              ),
            ],
            creatorFeature: CreatorFeatures.lists,
            Icon: ListsIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'lists',
          },
          {
            title: 'Expiring Fans',
            href: AppRoutes.Creator.Settings.ExpiringFans.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: ExpiringFansIcon,
            creatorFeature: CreatorFeatures.expiring_fans,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'expiring-fans',
          },
          {
            title: 'Online Mass Messages',
            href: AppRoutes.Creator.Settings.OnlineMassMessages.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: OnlineMassMessageIcon,
            creatorPermission: CreatorPermission.MODIFY_ONLINE_MASS_MESSAGES,
            creatorFeature: CreatorFeatures.bulk_messages,
            testId: 'online-mass-messages',
          },
          {
            title: 'Vault',
            href: AppRoutes.Creator.Settings.Vault.Index.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: VaultIcon,
            creatorFeature: CreatorFeatures.better_vault,
            creatorPermission: CreatorPermission.SEE_VAULT_IN_DASHBOARD,
            testId: 'vault',
          },
          {
            title: 'Display Settings',
            href: AppRoutes.Creator.Settings.DisplaySettings.asUrl(
              creatorIdPlaceholder,
            ),
            creatorFeature: CreatorFeatures.display_settings,
            Icon: DisplaySettingsIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'display-settings',
          },
          {
            title: 'Automation Settings',
            href: AppRoutes.Creator.Settings.AutomationSettings.asUrl(
              creatorIdPlaceholder,
            ),
            creatorFeature: CreatorFeatures.automation_settings,
            Icon: AutomationIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'automation-settings',
          },
          {
            title: 'Visibility',
            href: AppRoutes.Creator.Settings.VisibilitySettings.asUrl(
              creatorIdPlaceholder,
            ),
            creatorFeature: CreatorFeatures.visibility,
            Icon: VisibilityIcon,
            creatorPermission: CreatorPermission.MODIFY_SETTINGS,
            testId: 'visibility',
          },
        ],
      },
    ],
  },
  {
    separator: 'border',
    items: [
      {
        title: 'Accounting',
        href: AppRoutes.Accounting.Index.asUrl(),
        Icon: AccountingIcon,
        accountFeature: CreatorFeatures.accounting_site,
        onlyAdmin: true,
        testId: 'accounting',
      },
      {
        title: 'TikTok Research',
        Icon: IconTikTokResearch,
        href: '#',
        testId: 'tiktok-research',
        featureFlagName: FeatureFlagName.TIK_TOK_RESEARCH_FEATURE,
        children: [
          {
            title: 'Videos',
            href: AppRoutes.TiktokResearch.Videos.asUrl(creatorIdPlaceholder),
            Icon: IconTikTokVideos,
            onlyAdmin: true,
            accountFeature: CreatorFeatures.tiktok_research,
            featureFlagName: FeatureFlagName.TIK_TOK_RESEARCH_FEATURE,
            testId: 'tiktok-research-videos',
          },
          {
            title: 'Sounds',
            href: AppRoutes.TiktokResearch.Sounds.Index.asUrl(
              creatorIdPlaceholder,
            ),
            Icon: IconTikTokSounds,
            onlyAdmin: true,
            accountFeature: CreatorFeatures.tiktok_research,
            featureFlagName: FeatureFlagName.TIK_TOK_RESEARCH_FEATURE,
            testId: 'tiktok-research-sounds',
          },
          {
            title: 'My List',
            href: AppRoutes.TiktokResearch.Lists.Index.asUrl(
              creatorIdPlaceholder,
            ),
            onlyAdmin: true,
            accountFeature: CreatorFeatures.tiktok_research,
            Icon: IconTikTokLists,
            featureFlagName: FeatureFlagName.TIK_TOK_RESEARCH_FEATURE,
            testId: 'tiktok-research-lists',
          },
        ],
      },
      {
        title: 'Licenses',
        href: AppRoutes.Licenses.Index.asUrl(),
        Icon: LicenseIcon,
        onlyOwner: true,
        testId: 'licenses',
      },
      {
        title: 'VIP Support',
        href: AppRoutes.Help.VipSupport.asUrl(),
        Icon: VipSupportIcon,
        testId: 'vip-support',
      },
      {
        title: 'Support Center',
        href: AppRoutes.Help.Support.asUrl(),
        Icon: SupportCenterIcon,
        testId: 'support-center',
      },
    ],
  },
]

export const AUTOMATION_SUB_ITEMS: SubRoute[] = [
  {
    name: 'Promotion Reactivator',
    href: AppRoutes.Creator.Settings.Automations.Promotion.asUrl(
      creatorIdPlaceholder,
    ),
    testId: 'navbar-promotion-reactivator-item',
    icon: <PromotionReactivatorIcon />,
  },
  {
    name: 'Auto-Follow',
    href: AppRoutes.Creator.Settings.Automations.AutoFollow.asUrl(
      creatorIdPlaceholder,
    ),
    testId: 'navbar-auto-follow-item',
    creatorFeature: CreatorFeatures.auto_follow,
    icon: <AutoFollowIcon />,
  },
]

export const AUTOMATED_MESSAGES_SUB_ITEMS: SubRoute[] = [
  {
    name: 'Welcome Messages',
    href: AppRoutes.Creator.Settings.WelcomeMessages.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <IconMegaphone className="h-4 w-4" />,
    creatorFeature: CreatorFeatures.welcome_messages,
    testId: 'navbar-welcome-messages-item',
  },
  {
    name: 'PPV Follow-Ups',
    href: AppRoutes.Creator.Settings.PpvFollowUpMessages.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <PPVTrackingIcon className="h-4 w-4" />,
    creatorFeature: CreatorFeatures.follow_up_messages,
    testId: 'navbar-ppv-follow-ups-item',
  },
  {
    name: 'Rebill Messages',
    href: AppRoutes.Creator.Settings.RebillMessages.asUrl(creatorIdPlaceholder),
    icon: <RebillMessageIcon className="h-4 w-4" />,
    creatorFeature: CreatorFeatures.rebill_messages,
    testId: 'navbar-rebill-messages-item',
  },
]

export const LISTS_SUB_ITEMS: SubRoute[] = [
  {
    name: 'New Subs List',
    href: AppRoutes.Creator.Settings.Lists.NewSubsList.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <UserAddIcon className="h-4 w-4" />,
    testId: 'navbar-new-subs-list-item',
  },
  {
    name: 'Fan Spend Lists',
    href: AppRoutes.Creator.Settings.Lists.SpendingLists.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <IconSpendLIst className="h-4 w-4" />,
    creatorFeature: CreatorFeatures.tiers,
    testId: 'navbar-fan-spend-lists-item',
  },
  {
    name: 'No Spenders Lists',
    href: AppRoutes.Creator.Settings.Lists.NoSpenders.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <NoSpendersIcon />,
    testId: 'navbar-no-spenders-lists-item',
  },
  {
    name: 'Recent Spend Lists',
    href: AppRoutes.Creator.Settings.Lists.RecentSpenders.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <RecentSpendersIcon />,
    testId: 'navbar-recent-spend-lists-item',
  },
  {
    name: 'Shared Following List',
    href: AppRoutes.Creator.Settings.Lists.SharedFollowers.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <CreatorTransferIcon />,
    testId: 'navbar-shared-following-list-item',
  },
  {
    name: 'Custom Lists',
    href: AppRoutes.Creator.Settings.Lists.CustomLists.asUrl(
      creatorIdPlaceholder,
    ),
    icon: <CustomListsIcon />,
    testId: 'navbar-custom-lists-item',
  },
]

export const PROFILE_SUB_ITEMS: SubRoute[] = [
  {
    name: 'My Profile',
    href: AppRoutes.Profile.Settings.asUrl(),
    icon: <IconPerson className="h-4 w-4" />,
    testId: 'settings-my-profile-link',
  },
  {
    name: 'Preferences',
    href: AppRoutes.Profile.GeneralSettings.asUrl(),
    icon: <SettingsIcon className="h-4 w-4" />,
    testId: 'settings-preferences-link',
  },
  {
    name: 'Security',
    href: AppRoutes.Profile.Security.asUrl(),
    icon: <IconShield className="h-4 w-4" />,
    testId: 'settings-security-link',
  },
  {
    name: 'About CreatorHero',
    href: AppRoutes.Profile.AboutUs.asUrl(),
    icon: <IconBrandMask className="h-4 w-4" />,
    testId: 'settings-about-creatorhero-link',
  },
]
